/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

// Components imports
import { Space, Table, Typography } from 'antd';

// Misc imports
import { getCurrentQueryState } from '../../../store/utils/getCurrentQueryState';
import { useTranslation } from 'react-i18next';
import { useBotLanguage } from '../../../utils/hooks/useBotLanguage';

const ConfusionTable = ({ runTestSetResults }) => {
  const { t } = useTranslation();
  const { orgId, tenantId } = useParams();
  const { data: automatedAnswers, isFetching: isFetchingAutomatedAnswers } = getCurrentQueryState('fetchAutomatedAnswers', { orgId, tenantId });
  const { currentBotLanguage, primaryBotLanguage } = useBotLanguage();

  const [columns, setColumns] = useState([]);
  const [hoveredRow, setHoveredRow] = useState(null);

  useEffect(() => {
    if (!automatedAnswers?.items || !runTestSetResults?.confusionMatrix) {
      return;
    }

    const tableData = runTestSetResults?.confusionMatrix;
    let _columns = ([
      {
        title: t('Automated Answer'),
        dataIndex: 'rowKey',
        render: (_, record, index) => {
          const label = automatedAnswers?.items.find(i => i?.id === record.rowKey)?.name[currentBotLanguage] || automatedAnswers?.items.find(i => i?.id === record.rowKey)?.name[primaryBotLanguage] || record.rowKey;
          return <Space>
            <Typography.Text className="mr-4">
              {index + 1}
            </Typography.Text>
            <Typography.Text>
              {label}
            </Typography.Text>
          </Space>;
        },
        width: '20%'
      }
    ]);

    tableData.forEach((item, index) => {
      _columns.push({
        title: index + 1,
        width: '3%',
        dataIndex: ['values', item.rowKey],
        align: 'center'
      });
    });

    setColumns(_columns.map(col => ({
      ...col,
      onCell: (record, rowIndex) => {
        let matchingCells = [];

        if(hoveredRow) {
          const matchingColCells = Array.from({ length: hoveredRow+1 }, (_, i) => `${hoveredRow}-${i}`);
          const matchingRowCells = Array.from({ length: hoveredRow+1 }, (_, i) => `${i}-${hoveredRow}`);

          matchingCells = [...matchingColCells, ...matchingRowCells];
        }

        const currentColIndicator = col.dataIndex[1];
        let className = '';
        if (record.rowKey === currentColIndicator && record.values[currentColIndicator]) {
          className = 'text-success';
        } else if (record.values[currentColIndicator]) {
          className = 'text-error';
        }

        return {
          className: `${className} ${matchingCells.includes(`${col.title-1}-${rowIndex}`) ? 'bg-layout-gray fw-600' : ''}`,
          onMouseEnter: () => setHoveredRow(rowIndex),
          onMouseLeave: () => setHoveredRow(null)
        };
      }
    })));
  }, [automatedAnswers?.items, runTestSetResults?.confusionMatrix, hoveredRow]);

  return (
    <Table
      size="small"
      dataSource={runTestSetResults?.confusionMatrix}
      columns={columns}
      className="scrollbar-custom scrollbar-custom-nohide"
      scroll={{
        y: 500,
        x: 'max-content'
      }}
      loading={isFetchingAutomatedAnswers}
      pagination={false}
    />
  );
};

ConfusionTable.propTypes = {
  runTestSetResults: PropTypes.array
};

export default ConfusionTable;
